import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faFileExport, faClose } from '@fortawesome/free-solid-svg-icons';

import RankingItem from './RankingItem';
import { NotificationMessage } from './NotificationMessage';
import { InputMask } from '@react-input/mask';

registerLocale('pt-BR', ptBR);

function RankingList() {
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRanking, setSelectedRankig] = useState([]);

    const [notification, setNotification] = useState({
        type: 'info',
        message: '',
        show: false,
    });

    const [filters, setFilters] = useState({
        nome: '',
        cpf: '',
        rg: '',
        fone: '',
        razao: '',
        pontos: '',
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [openDateRange, setOpenDateRange] = useState(false);
    const [resultsPerPage, setResultsPerPage] = useState(20);

    const navigate = useNavigate();
    const location = useLocation();

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setFilters(prev => ({
            ...prev,
            dateFrom: start ? format(start, 'dd/MM/yyyy') : '',
            dateTo: end ? format(end, 'dd/MM/yyyy') : '',
        }));
    };

    const updateUrlParams = (newFilters) => {
        const filteredParams = Object.fromEntries(
            Object.entries(newFilters).filter(([_, value]) => value !== '' && value !== null)
        );

        const params = new URLSearchParams(filteredParams).toString();
        navigate(`?${params}`);
    };

    const fetchRanking = async () => {
        try {
            const accessToken = localStorage.getItem('access_token');

            const response = await fetch(`${baseUrl}/manager/participantes/ranking`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os ranking.');
            }

            const data = await response.json();
            setRanking(data.ranking);
            console.log(data.ranking)
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        for (const param of searchParams) {
            setFilters(prev => ({
                ...prev,
                [param[0]]: param[1]
            }));
        }
        fetchRanking();
    }, []);

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
    };

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            setSelectedRankig(ranking.map(member => member.id));
        } else {
            setSelectedRankig([]);
        }
    };

    const isAllSelected = selectedRanking.length === ranking.length && ranking.length > 0;

    const handleSelectVenda = (vendaId) => {
        setSelectedRankig(prevSelected =>
            prevSelected.includes(vendaId)
                ? prevSelected.filter(id => id !== vendaId)
                : [...prevSelected, vendaId]
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
        };
        setFilters(updatedFilters);
        updateUrlParams(updatedFilters);
    };

    const handleExport = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/relatorios/participantes-ranking`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao exportar relatório!',
                    show: true,
                });
                throw new Error('Erro ao exportar relatório.');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'relatorio_ranking.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setNotification({
                type: 'success',
                message: `Relatório do ranking exportado com sucesso!`,
                show: true
            });
        } catch (error) {
            console.error('Erro ao exportar:', error);
        }
    };

    const filteredRanking = useMemo(() => {
        return ranking.filter(r => {
            const matchesNome = r.nome.toLowerCase().includes(filters.nome);
            const matchesCpf = r.cpf.toLowerCase().includes(filters.cpf.toLowerCase());
            const matchesRg = r.rg.toString().includes(filters.rg);
            const matchesTelefone = r.fone.toString().includes(filters.fone);
            const matchesRazao = r.razao.toLowerCase().includes(filters.razao.toLowerCase());
            const matchesPontos = r.pontos
                ? r.pontos.toString().includes(filters.pontos) : '0';
            return matchesNome && matchesCpf && matchesRg && matchesTelefone && matchesRazao && matchesPontos;
        });
    }, [ranking, filters]);

    const columns = useMemo(() => [
        { Header: 'Nome', accessor: 'nome', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'CPF', accessor: 'cpf', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'RG', accessor: 'rg', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Telefone', accessor: 'fone', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Loja', accessor: 'razao', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Pontos', accessor: 'pontos', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
    ], [isAllSelected, selectedRanking]);

    const pageParams = new URLSearchParams(window.location.search);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { pageIndex },
        gotoPage,
        nextPage,
        previousPage,
    } = useTable(
        {
            columns,
            data: filteredRanking,
            initialState: { pageIndex: pageParams.has('page') ? (pageParams.get('page') - 1) : 0, pageSize: resultsPerPage },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        if (location.state?.message) {
            setNotification({
                type: location.state.message.type,
                message: location.state.message.text,
                show: true,
            });
        }
    }, [location.state]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', pageIndex + 1);
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pageIndex, navigate]);

    if (loading) return (
        <div className="mx-auto py-10">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
    );

    if (error) {
        return <div className="text-2xl text-center text-red-600 font-bold my-10">{error}</div>;
    }

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                        type="text"
                        id="nome"
                        name="nome"
                        placeholder="Nome"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.nome}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                    <InputMask
                        type="text"
                        id="cpf"
                        name="cpf"
                        placeholder="CPF"
                        mask="___.___.___-__"
                        replacement={{ _: /\d/ }}
                        value={filters.cpf}
                        onChange={handleFilterChange}
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    />
                </div>

                <div>
                    <label htmlFor="rg" className="block text-sm font-medium text-gray-700 mb-1">RG</label>
                    <input
                        type="text"
                        id="rg"
                        name="rg"
                        placeholder="RG"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.rg}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="fone" className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <InputMask
                        type="text"
                        id="fone"
                        name="fone"
                        placeholder="Telefone"
                        mask="(__) _____-____"
                        replacement={{ _: /\d/ }}
                        value={filters.fone}
                        onChange={handleFilterChange}
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    />
                </div>

                <div>
                    <label htmlFor="razao" className="block text-sm font-medium text-gray-700 mb-1">Razão Social</label>
                    <input
                        type="text"
                        id="razao"
                        name="razao"
                        placeholder="Razão Social"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.razao}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>


            <table {...getTableProps()} className="w-full table-auto border-collapse bg-white shadow-md rounded-lg">
                <thead>
                    {headerGroups.map((headerGroup, index) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={key || index} {...headerGroupProps} className="bg-gray-100 text-left">
                                {headerGroup.headers.map((column, groupIndex) => {
                                    const { key: colKey, ...columnProps } = column.getHeaderProps(
                                        column.getSortByToggleProps({ title: undefined })
                                    );
                                    return (
                                        <th
                                            key={colKey || groupIndex}
                                            {...columnProps}
                                            className="px-4 py-2 font-semibold text-gray-700 border-b"
                                        >
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg
                                                        className="fill-gray-500 inline-block mb-1 ml-1"
                                                        width="10"
                                                        height="5"
                                                        viewBox="0 0 10 5"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M5 0L0 5H10L5 0Z" fill="" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="fill-gray-500 inline-block mb-1 ml-1"
                                                        width="10"
                                                        height="5"
                                                        viewBox="0 0 10 5"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill="" />
                                                    </svg>
                                                )
                                            ) : (
                                                ''
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <RankingItem
                                key={row.id}
                                index={index}
                                row={row}
                                onSelect={handleSelectVenda}
                                isSelected={selectedRanking.includes(row.original.id)}
                            />
                        );
                    })}
                </tbody>
            </table>

            <div className="flex items-center mt-6 mb-4">
                <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                    <button
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"></path>
                        </svg>
                    </button>

                    {pageIndex !== 0 && (
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                        >
                            1
                        </button>
                    )}

                    {pageIndex > 1 && pageOptions.length > 4 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageOptions[pageIndex - 1] ? (
                        <button
                            onClick={() => gotoPage(pageIndex - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex}
                        </button>
                    ) : null}


                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-slate-700">
                        {pageIndex + 1}
                    </span>

                    {pageOptions[pageIndex + 1] && (
                        <button
                            onClick={() => gotoPage(pageIndex + 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex + 2}
                        </button>
                    )}

                    {pageIndex < pageOptions.length - 3 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageIndex < pageOptions.length - 2 && (
                        <button
                            onClick={() => gotoPage(pageOptions.length - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageOptions.length}
                        </button>
                    )}

                    <button
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canNextPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Próximo</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"></path>
                        </svg>
                    </button>
                </nav>

                <div className="flex ml-auto pl-10 gap-3">
                    <div className="relative">
                        <button 
                            onClick={() => handleExport()}
                            className="bg-blue-500 text-white text-sm 2xl:text-base px-4 h-9 rounded transition-all hover:bg-blue-600"
                        >
                            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                            Exportar
                        </button>
                    </div>
                </div>
            </div>

            {notification && notification.show && (
                <NotificationMessage
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                    onClose={handleCloseNotification}
                />
            )}
        </>
    );
}

export default RankingList;
